// apps/web/src/components/Sidebar.tsx

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { trpc } from '@/trpc/client';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  Package,
  PlusCircle,
  Truck,
  User,
  Briefcase,
  MapPin,
  Wallet,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Activity,
  Clock,
  Newspaper,
  FileText,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

interface NavItem {
  label: string;
  icon: React.ElementType;
  href: string;
  badge?: number;
}

const SIDEBAR_STATE_KEY = 'sidebar-collapsed';

const Sidebar: React.FC = () => {
  const { user, logout } = useAuthStore();
  const location = useLocation();

  // Load initial state from localStorage, default to collapsed
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_STATE_KEY);
    return saved !== null ? saved === 'true' : true; // Default to collapsed
  });

  // Fetch in-progress jobs for transporters
  const { data: inProgressJobs } = trpc.booking.getMyActiveBookings?.useQuery(
    undefined,
    {
      enabled: !!user && user.role === 'TRANSPORTER',
      refetchInterval: !!user && user.role === 'TRANSPORTER' ? 60_000 : false,
      staleTime: 25_000,
    }
  );

  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(SIDEBAR_STATE_KEY, String(isCollapsed));
  }, [isCollapsed]);

  // Hide sidebar for admin users
  if (!user || user.role === 'ADMIN') return null;

  // ═══════════════════════════════════════════════════════════════════════════
  // NAVIGATION ITEMS BY ROLE
  // ═══════════════════════════════════════════════════════════════════════════

  const clientNavItems: NavItem[] = [
    { label: 'Dashboard', icon: Home, href: '/dashboard' },
    { label: 'Browse Jobs', icon: Briefcase, href: '/jobs' },
    { label: 'Blog', icon: Newspaper, href: '/blog' },
    { label: 'Post New Job', icon: PlusCircle, href: '/jobs/create' },
    { label: 'My Wallet', icon: Wallet, href: '/dashboard/wallet' },
    { label: 'Cash Slips', icon: FileText, href: '/dashboard/receipts' },
    { label: 'Profile', icon: User, href: '/dashboard/profile' },
    { label: 'Settings', icon: Settings, href: '/dashboard/settings' },
  ];

  const transporterNavItems: NavItem[] = [
    { label: 'Dashboard', icon: Home, href: '/dashboard' },
    { label: 'Find Jobs', icon: MapPin, href: '/jobs' },
    { label: 'Blog', icon: Newspaper, href: '/blog' },
    {
      label: 'Active Deliveries',
      icon: Activity,
      href: '/dashboard/transporter',
      badge: inProgressJobs?.length || 0
    },
    { label: 'My Vehicles', icon: Truck, href: '/dashboard/my-vehicles' },
    { label: 'My Wallet', icon: Wallet, href: '/dashboard/wallet' },
    { label: 'Cash Slips', icon: FileText, href: '/dashboard/receipts' },
    { label: 'Profile', icon: User, href: '/dashboard/profile' },
    { label: 'Settings', icon: Settings, href: '/dashboard/settings' },
  ];

  const fleetManagerNavItems: NavItem[] = [
    { label: 'Fleet Command', icon: Home, href: '/dashboard/fleet' },
    { label: 'Find Jobs', icon: Briefcase, href: '/jobs' }, // Drivers don't see this, Managers do.
    { label: 'Blog', icon: Newspaper, href: '/blog' },
    { label: 'My Vehicles', icon: Truck, href: '/dashboard/my-vehicles' },
    { label: 'My Wallet', icon: Wallet, href: '/dashboard/wallet' },
    { label: 'Cash Slips', icon: FileText, href: '/dashboard/receipts' },
    { label: 'Profile', icon: User, href: '/dashboard/profile' },
    { label: 'Settings', icon: Settings, href: '/dashboard/settings' },
  ];

  const driverNavItems: NavItem[] = [
    { label: 'Mission Control', icon: Home, href: '/dashboard/driver' },
    { label: 'Profile', icon: User, href: '/dashboard/profile' },
    { label: 'Settings', icon: Settings, href: '/dashboard/settings' },
  ];

  let navItems = clientNavItems;
  if (user.role === 'TRANSPORTER') navItems = transporterNavItems;
  else if (user.role === 'FLEET_MANAGER') navItems = fleetManagerNavItems;
  else if (user.role === 'FLEET_DRIVER') navItems = driverNavItems;

  const initials = user.profile?.fullName
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || user.email?.slice(0, 2).toUpperCase();

  return (
    <motion.aside
      initial={false}
      animate={{
        width: isCollapsed ? '80px' : '256px',
      }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="hidden md:flex flex-col bg-card border-r border-border h-screen sticky top-0 relative z-40 pt-24"
    >
      {/* Collapse Toggle Button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-20 z-10 w-6 h-6 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:bg-primary/90 transition-colors"
        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? (
          <ChevronRight className="w-4 h-4" />
        ) : (
          <ChevronLeft className="w-4 h-4" />
        )}
      </button>


      {/* Navigation Links */}
      <ScrollArea className="flex-1 px-3">
        <nav className="py-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            const showBadge = item.badge && item.badge > 0;

            return (
              <Link
                key={item.href}
                to={item.href}
                id={`sidebar-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                title={isCollapsed ? item.label : undefined}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all relative group',
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                {isActive && !isCollapsed && (
                  <motion.div
                    layoutId="activeSidebarTab"
                    className="absolute left-0 top-0 bottom-0 w-1 bg-primary-foreground rounded-r-full"
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                )}

                <Icon className="w-5 h-5 flex-shrink-0" />

                <AnimatePresence>
                  {!isCollapsed && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      className="text-sm font-medium whitespace-nowrap"
                    >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>

                {showBadge && !isCollapsed && (
                  <Badge
                    variant="destructive"
                    className="ml-auto text-[10px] px-1.5 py-0 h-5 min-w-5"
                  >
                    {item.badge! > 99 ? '99+' : item.badge}
                  </Badge>
                )}

                {showBadge && isCollapsed && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
                )}

                {isCollapsed && (
                  <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                    {item.label}
                    {showBadge && (
                      <span className="ml-2 text-red-500 font-bold">
                        ({item.badge})
                      </span>
                    )}
                  </div>
                )}
              </Link>
            );
          })}
        </nav>
      </ScrollArea>

      <Separator />

      {/* In-Progress Jobs Quick View */}
      {user.role === 'TRANSPORTER' && inProgressJobs && inProgressJobs.length > 0 && !isCollapsed && (
        <>
          <div className="px-4 py-3 space-y-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="w-3 h-3" />
              <span className="font-medium">In Progress</span>
            </div>
            <ScrollArea className="max-h-32">
              <div className="space-y-1">
                {inProgressJobs.slice(0, 3).map((booking: any) => (
                  <Link
                    key={booking.id}
                    to={`/jobs/${booking.jobId}`}
                    className="block p-2 rounded-md bg-muted/50 hover:bg-muted transition-colors text-xs"
                  >
                    <div className="font-medium truncate">
                      {booking.job?.title || 'Delivery'}
                    </div>
                    <div className="text-muted-foreground text-[10px] flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3" />
                      {booking.job?.pickupDistrict} → {booking.job?.deliveryDistrict}
                    </div>
                  </Link>
                ))}
                {inProgressJobs.length > 3 && (
                  <Link
                    to="/dashboard/transporter"
                    className="block p-2 text-center text-xs text-primary hover:underline"
                  >
                    +{inProgressJobs.length - 3} more
                  </Link>
                )}
              </div>
            </ScrollArea>
          </div>
          <Separator />
        </>
      )}

      {/* Logout Button */}
      <div className="p-3">
        <Button
          variant="ghost"
          size={isCollapsed ? 'icon' : 'default'}
          className={cn(
            'relative group text-destructive hover:text-destructive hover:bg-destructive/10',
            isCollapsed ? 'w-full' : 'w-full justify-start'
          )}
          onClick={logout}
        >
          <LogOut className="w-5 h-5" />
          {!isCollapsed && <span className="ml-3">Logout</span>}

          {isCollapsed && (
            <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
              Logout
            </div>
          )}
        </Button>
      </div>
    </motion.aside>
  );
};

export default Sidebar;